import {
  parseStandardAPIErrorMessage,
  validatePostAPIResponse,
  validateGetAPIResponse,
} from "../util/CommonUtil";
import BaseApiService from "./BaseApiService";
import { ActionType, ScreenRenderStatus } from "../util/CommonConstant";
import { linkBluejayApiBase } from "../routes";
import HelperService from "./HelperService";

class PageUploadAudioAPIService extends BaseApiService {
  dispatchScreenRenderStatusAction(
    dispatch: any,
    screenRenderStatus: ScreenRenderStatus = ScreenRenderStatus.LOADING
  ) {
    dispatch({
      type: ActionType.SET_SCREEN_RENDER_STATUS,
      payload: screenRenderStatus,
    });
  }

  dispatchErrorMessageAction(dispatch: any, errorMessage = "") {
    dispatch({
      type: ActionType.SET_ERROR_MESSAGE,
      payload: errorMessage,
    });
  }

  async handleApiError(dispatch: any, error: any) {
    this.dispatchScreenRenderStatusAction(dispatch, ScreenRenderStatus.ERROR);
    this.dispatchErrorMessageAction(dispatch, parseStandardAPIErrorMessage(error));
  }

  async uploadAudio(file: File, dispatch: any): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      dispatch({
        type: ActionType.SET_IS_UPLOADING,
        payload: true,
      });

      const url = `${linkBluejayApiBase}/api/upload-audio`;
      const baseUrl = this.getDefaultApiUrl() || "http://localhost:8000";
      const fullUrl = `${baseUrl}${url}`;

      const response = await this.getAxios().post(fullUrl, formData, {
        headers: HelperService.getHeadersWithAuthWithMultipartFormData(),
      });

      if (validatePostAPIResponse(response)) {
        return response;
      }
    } catch (error: any) {
      this.handleApiError(dispatch, error);
      throw error;
    } finally {
      dispatch({
        type: ActionType.SET_IS_UPLOADING,
        payload: false,
      });
    }
  }

  async getStatus(audioName: string): Promise<any> {
    try {
      const url = `${linkBluejayApiBase}/api/status/${audioName}`;
      const baseUrl = this.getDefaultApiUrl() || "http://localhost:8000";
      const fullUrl = `${baseUrl}${url}`;

      const response = await this.getAxios().get(fullUrl, {
        headers: HelperService.getHeadersWithAuth(),
      });

      if (validateGetAPIResponse(response)) {
        return response.data;
      }
    } catch (error: any) {
      console.error("Error fetching status:", error);
      throw error;
    }
  }

  async uploadAndAnalyze(file: File, dispatch: any): Promise<any> {
    return this.uploadAndAnalyzeMultiple([file], dispatch);
  }

  async uploadAndAnalyzeMultiple(
    files: File[],
    dispatch: any,
    predefinedAudioNames?: string[]
  ): Promise<any> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    if (predefinedAudioNames && predefinedAudioNames.length > 0) {
      formData.append("predefined_pdf_names", JSON.stringify(predefinedAudioNames));
    }
    formData.append("apply_policies", "true");

    try {
      dispatch({
        type: ActionType.SET_IS_UPLOADING,
        payload: true,
      });

      const url = `${linkBluejayApiBase}/api/upload-and-analyze`;
      const baseUrl = this.getDefaultApiUrl() || "http://localhost:8000";
      const fullUrl = `${baseUrl}${url}`;

      const response = await this.getAxios().post(fullUrl, formData, {
        headers: HelperService.getHeadersWithAuthWithMultipartFormData(),
      });

      if ((response?.status === 200 || response?.status === 201 || response?.status === 202) && response?.data) {
        return response;
      }

      throw new Error(`Invalid response: status ${response?.status}, has data: ${!!response?.data}`);
    } catch (error: any) {
      if (error?.response) {
        throw error;
      }
      this.handleApiError(dispatch, error);
      throw error;
    } finally {
      dispatch({
        type: ActionType.SET_IS_UPLOADING,
        payload: false,
      });
    }
  }

  async getJobResult(job_id: string): Promise<any> {
    try {
      const url = `${linkBluejayApiBase}/api/job/${job_id}`;
      const baseUrl = this.getDefaultApiUrl() || "http://localhost:8000";
      const fullUrl = `${baseUrl}${url}`;

      const response = await this.getAxios().get(fullUrl, {
        headers: HelperService.getHeadersWithAuth(),
      });

      if (validateGetAPIResponse(response)) {
        return response.data;
      }
    } catch (error: any) {
      console.error("Error fetching job result:", error);
      throw error;
    }
  }

  async getAudioProgress(urlValue: string): Promise<any> {
    try {
      const url = `${linkBluejayApiBase}/api/audio/progress`;
      const baseUrl = this.getDefaultApiUrl() || "http://localhost:8000";
      const fullUrl = `${baseUrl}${url}`;
      const response = await this.getAxios().get(fullUrl, {
        params: { url: urlValue },
        headers: HelperService.getHeadersWithAuth(),
      });
      if (validateGetAPIResponse(response)) {
        return response.data;
      }
    } catch (error: any) {
      console.error("Error fetching audio progress:", error);
      throw error;
    }
  }

  async listWorkflowStatuses(
    page = 1,
    pageSize = 10
  ): Promise<{
    statuses: Array<{
      id?: string;
      fileUrl?: string;
      steps?: Record<string, any>;
      createdAt?: string;
      updatedAt?: string;
    }>;
    pagination: {
      page: number;
      pageSize: number;
      returnedCount: number;
      totalCount: number;
      hasNext: boolean;
    };
  }> {
    const emptyResult = {
      statuses: [],
      pagination: { page, pageSize, returnedCount: 0, totalCount: 0, hasNext: false },
    };
    try {
      const url = `${linkBluejayApiBase}/api/workflows/status`;
      const baseUrl = this.getDefaultApiUrl() || "http://localhost:8000";
      const fullUrl = `${baseUrl}${url}`;
      const response = await this.getAxios().get(fullUrl, {
        params: { page, page_size: pageSize },
        headers: HelperService.getHeadersWithAuth(),
      });
      if (validateGetAPIResponse(response) && response.data) {
        return {
          statuses: response.data.statuses || [],
          pagination: response.data.pagination || emptyResult.pagination,
        };
      }
      return emptyResult;
    } catch (error: any) {
      console.error("Error fetching workflow statuses:", error);
      throw error;
    }
  }

  async getPredefinedAudios(): Promise<{
    audio_names?: Array<{ name: string }>;
    pdf_names?: Array<{ name: string }>;
  }> {
    try {
      const url = `${linkBluejayApiBase}/api/predefined-pdfs`;
      const baseUrl = this.getDefaultApiUrl() || "http://localhost:8000";
      const fullUrl = `${baseUrl}${url}`;
      const response = await this.getAxios().get(fullUrl, {
        headers: HelperService.getHeadersWithAuth(),
      });
      if (validateGetAPIResponse(response) && response.data) {
        return response.data;
      }
      return { audio_names: [] };
    } catch (error: any) {
      console.error("Error fetching predefined audio files:", error);
      return { audio_names: [] };
    }
  }
}

export default new PageUploadAudioAPIService();
